// Defend / interception. Pure. No React, no Three.js.
//
// ── Codex (Defend). Matched word for word ──
//
//   "Defend is a triggered ability with the following text: When an
//   enemy attacks another unit or site in this unit's range of motion,
//   it may tap to defend, moving to the attack's location and joining
//   the fight."
//
//   "When Exactly is a Unit Allowed to Defend?: If the unit is untapped
//   and the attack's location is within its range of motion."
//
//   "Range of Motion: A unit's range of motion is every location it
//   could reach if it used the Move and Attack basic ability, ignoring
//   all triggered abilities. If a unit can't use the Move and Attack
//   basic ability (e.g. Disabled), then it has no range of motion."
//
// ── Eligibility, exactly as the codex states it ──
//
//   The personal-state gate (canDefend) is ONLY:
//     • It's a unit. A Minion or an Avatar. ("A unit must be untapped
//       to defend." Sites and Artifacts aren't units, so they can't.)
//     • It's untapped. ("the unit is untapped")
//     • It has a range of motion. I.e. it's NOT Disabled. ("If a unit
//       can't use the Move and Attack basic ability (e.g. Disabled),
//       then it has no range of motion.")
//
//   Everything else is geometry, handled by getEligibleDefenders:
//     • Controlled by the same player as the original target (you defend
//       your own allies).
//     • Not the original target itself; not the attacker.
//     • "the attack's location is within its range of motion." Already
//       on the attack's surface location (zero steps), OR the reach set
//       (Move and Attack lookahead: one step + Movement +X, Burrowing,
//       Submerge, Voidwalk, Airborne diagonals, …) includes it.
//
//   Plus summoning sickness: the flag expires at every turn boundary,
//   so a flagged unit can only have entered the realm DURING the
//   current (attacker's) turn. An effect summon. Sick minions "cannot
//   tap, or be tapped, to pay for costs" (codex) and Defend taps as its
//   cost. (A minion summoned on its controller's own turn sheds the
//   flag at the boundary. "you may use it to defend on your
//   opponent's turn" stays true.) Mirrors server filterDefenders.
//
//   Deliberately NOT gated (these are not in the rule):
//     • Airborne. "Airborne attacks ... can be defended by any unit,
//       even without Airborne." (The airborne gate lives in combat.js
//       targeting and intercept.js, not here.)
//     • Subsurface. A Burrowed/Submerged unit defends iff the surface
//       attack location is in its range of motion (it surfaces via its
//       movement ability). That's a reach question, not a flat ban, so
//       subsurface units go through the reach path (never the zero-step
//       on-cell fast-path, since they aren't on the surface yet).
//
// ── Out of scope (deferred) ──
//
//   • Stealth defender: a Stealth minion CAN defend per codex
//     (defending is interacting with the realm), but doing so
//     consumes its Stealth token. The caller handles the consume.
//   • Defend vs projectile attacks: still allowed. The defender
//     intercepts the projectile by becoming the new target. The
//     Ranged attacker's projectile rule still applies (no
//     strike-back), so a defender intercepting a projectile takes
//     damage but doesn't get to strike back at distance.

import { isDisabled } from '../automation/scope';
import { hasKeyword } from './keywordQuery';
import { hasActiveStealth } from './stealth';
import { hasSummoningSickness } from './summoningSickness';
import { isUndefendable } from '../automation/ops/cards/kissOfJudas';
import { countsAsUnit } from './typeline';
import { unitFootprintCells } from './footprint';

/**
 * True iff this card MEETS the personal-state requirements to defend
 * Independent of geometry. Word-for-word with the codex: an untapped
 * unit that has a range of motion (i.e. not Disabled). Caller still
 * checks controller / reach / not-self. A "unit" is a Minion or an
 * Avatar. Including Automatons (artifacts that are minions).
 */
export function canDefend(card) {
  if (!card) return false;
  if (!countsAsUnit(card)) return false; // "A unit must be untapped to defend"
  if (card.tapped) return false;                // "the unit is untapped"
  if (hasSummoningSickness(card)) return false; // entered THIS turn. Can't tap to pay (charge-aware)
  if (isDisabled(card)) return false;           // Disabled → no range of motion
  // Printed "Can't defend" (Tawny, Recurring Specter, Lumbering Giant).
  // Read via hasKeyword so silencing the card lifts the restriction with
  // the rest of its box text.
  if (hasKeyword(card, 'cant_defend')) return false;
  return true;
}

/**
 * Enumerate candidate defenders for an attack on `originalTarget` at
 * cell `(targetCol, targetRow)`. Walks the live mesh map filtering by
 * controller + canDefend + reach.
 *
 * @param attackerCard the unit doing the attacking
 * @param originalTarget the card being attacked (Minion / Avatar / Site)
 * @param targetCol/targetRow cell of the original target
 * @param targetRegion the attack's region ('surface' for a site / surface
 *        unit; 'underground' / 'underwater' for a subsurface attack). The
 *        defender must reach this exact location (codex Defend: "moving to
 *        the attack's location"), so surfacing into a surface attack costs
 *        its own step.
 * @param meshes live Three.js mesh map
 * @param cellAt (x, z) → { col, row }
 * @param reachComputer (card) → Set<"col,row,region"> of region-qualified
 *        locations the card can reach. Caller supplies this. It bundles the
 *        existing movement machinery (Movement +N, Voidwalk, region-step
 *        costs, etc.) without dragging it into this pure module.
 * @returns Array of { card, mesh, fromCell, mustMove }
 */
export function getEligibleDefenders(
  attackerCard, originalTarget, targetCol, targetRow, targetRegion,
  meshes, cellAt, reachComputer,
) {
  if (!originalTarget) return [];
  // Codex Stealth: "their attacks cannot be defended." A Stealth
  // attacker's attack offers no defend window at all.
  if (hasActiveStealth(attackerCard)) return [];
  // Kiss of Judas: "attacks against target Avatar can't be defended"
  // this turn. The marked target offers no defend window either.
  if (isUndefendable(originalTarget)) return [];
  const region = targetRegion || 'surface';
  // Defender's controller must match the original target's controller.
  // (Defending an ally. Convention: !!rotated maps to controller.)
  const targetControllerRotated = !!originalTarget.rotated;
  const out = [];
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card) continue;
    if (card.id === originalTarget.id) continue;
    if (card.id === attackerCard?.id) continue;
    if (!!card.rotated !== targetControllerRotated) continue;
    if (!canDefend(card)) continue;
    const fromCell = cellAt(mesh.position.x, mesh.position.z);
    if (!fromCell) continue;
    // Already AT the attack's LOCATION (cell AND region) → zero-step defend.
    // A multi-cell unit (animated aura, etc.) qualifies if ANY cell it
    // occupies is the attack cell. "At the region" = a surface unit for a
    // surface attack, or a subsurface unit sharing the attack's subsurface
    // region. A subsurface unit under a SURFACE attack isn't there yet. It
    // must surface (a move), so it falls through to the reach path.
    const occupied = unitFootprintCells(card, fromCell);
    const onTargetCell = occupied.some((c) => c.col === targetCol && c.row === targetRow);
    const atAttackRegion = card.subsurface ? card.subsurface === region : region === 'surface';
    if (onTargetCell && atAttackRegion) {
      out.push({ card, mesh, fromCell, mustMove: false });
      continue;
    }
    // Otherwise the attack's exact location must be within its range of
    // motion. The reach set is region-qualified, so surfacing into a surface
    // attack (or diving into a subsurface one) costs its own step.
    const reach = reachComputer ? reachComputer(card) : null;
    if (reach && reach.has(`${targetCol},${targetRow},${region}`)) {
      out.push({ card, mesh, fromCell, mustMove: true });
    }
  }
  return out;
}
